import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

interface RendezVous {
  id: string;
  date_rdv: string;
  heure_debut: string;
  heure_fin: string;
  statut: string;
  patient_id: string;
  medecin_id: string;
  type_rdv?: string;
  patients: { nom: string; prenom: string; telephone?: string; email?: string } | null;
  medecins: { nom: string; prenom: string; specialite: string } | null;
}

interface Medecin {
  id: string;
  nom: string;
  prenom: string;
  specialite: string;
}

interface CalendarWeekViewProps {
  rendezVous: RendezVous[];
  medecins: Medecin[];
  onModify?: (rdv: RendezVous) => void;
}

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 07h → 19h
const PX_PER_HOUR = 72;
const START_HOUR = 7;

const DOCTOR_COLORS = [
  { block: 'bg-blue-500 border-blue-600', text: 'text-white' },
  { block: 'bg-emerald-500 border-emerald-600', text: 'text-white' },
  { block: 'bg-rose-500 border-rose-600', text: 'text-white' },
  { block: 'bg-amber-500 border-amber-600', text: 'text-white' },
  { block: 'bg-violet-500 border-violet-600', text: 'text-white' },
  { block: 'bg-cyan-500 border-cyan-600', text: 'text-white' },
  { block: 'bg-orange-500 border-orange-600', text: 'text-white' },
  { block: 'bg-teal-500 border-teal-600', text: 'text-white' },
];

const JOURS_COURT = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getWeekDates(monday: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function toDateString(date: Date): string {
  return date.toISOString().split('T')[0];
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function getBlockStyle(heure_debut: string, heure_fin: string): { top: number; height: number } {
  const startMin = timeToMinutes(heure_debut);
  const endMin = timeToMinutes(heure_fin);
  const offsetMin = startMin - START_HOUR * 60;
  const durationMin = endMin - startMin;
  return {
    top: (offsetMin / 60) * PX_PER_HOUR,
    height: Math.max((durationMin / 60) * PX_PER_HOUR, 30),
  };
}

function formatWeekRange(monday: Date): string {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  return `${monday.toLocaleDateString('fr-FR', opts)} — ${sunday.toLocaleDateString('fr-FR', opts)} ${sunday.getFullYear()}`;
}

export default function CalendarWeekView({ rendezVous, medecins, onModify }: CalendarWeekViewProps) {
  const navigate = useNavigate();
  const [currentMonday, setCurrentMonday] = useState<Date>(() => getMonday(new Date()));
  const [filterMedecinId, setFilterMedecinId] = useState('');
  const [selectedRdv, setSelectedRdv] = useState<RendezVous | null>(null);

  const weekDates = useMemo(() => getWeekDates(currentMonday), [currentMonday]);

  const doctorColorMap = useMemo(() => {
    const map: Record<string, number> = {};
    medecins.forEach((m, i) => { map[m.id] = i % DOCTOR_COLORS.length; });
    return map;
  }, [medecins]);

  const filteredRdv = useMemo(() => {
    if (!filterMedecinId) return rendezVous;
    return rendezVous.filter(r => r.medecin_id === filterMedecinId);
  }, [rendezVous, filterMedecinId]);

  const getRdvForDay = (dateStr: string): RendezVous[] =>
    filteredRdv.filter(r => r.date_rdv === dateStr && r.statut !== 'annule');

  const prevWeek = () => {
    const d = new Date(currentMonday);
    d.setDate(d.getDate() - 7);
    setCurrentMonday(d);
  };

  const nextWeek = () => {
    const d = new Date(currentMonday);
    d.setDate(d.getDate() + 7);
    setCurrentMonday(d);
  };

  const goToToday = () => setCurrentMonday(getMonday(new Date()));

  const todayStr = new Date().toISOString().split('T')[0];

  const getStatusBadge = (statut: string) => {
    switch (statut) {
      case 'en_attente': return 'bg-yellow-100 text-yellow-800';
      case 'confirme': return 'bg-green-100 text-green-800';
      case 'termine': return 'bg-gray-100 text-gray-600';
      case 'manque': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatusLabel = (statut: string) => {
    switch (statut) {
      case 'en_attente': return 'En attente';
      case 'confirme': return 'Confirmé';
      case 'termine': return 'Terminé';
      case 'manque': return 'Manqué';
      case 'annule': return 'Annulé';
      default: return statut;
    }
  };

  // Count RDV per day for header badges
  const rdvCountPerDay = useMemo(() => {
    const map: Record<string, number> = {};
    weekDates.forEach(d => {
      const ds = toDateString(d);
      map[ds] = filteredRdv.filter(r => r.date_rdv === ds && r.statut !== 'annule').length;
    });
    return map;
  }, [weekDates, filteredRdv]);

  return (
    <div className="bg-white rounded-3xl border-2 border-clinic-blue/10 shadow-xl overflow-hidden">
      {/* ── Top controls ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 md:px-6 py-4 border-b border-gray-100">
        {/* Week navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={prevWeek}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg text-gray-600"></i>
          </button>
          <span className="text-sm md:text-base font-semibold text-clinic-blue-dark px-2 whitespace-nowrap">
            {formatWeekRange(currentMonday)}
          </span>
          <button
            onClick={nextWeek}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 transition-all cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg text-gray-600"></i>
          </button>
          <button
            onClick={goToToday}
            className="ml-1 px-4 py-2 text-xs font-semibold bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark transition-all cursor-pointer whitespace-nowrap"
          >
            Aujourd'hui
          </button>
        </div>

        {/* Doctor filter */}
        <div className="flex items-center gap-2">
          <select
            value={filterMedecinId}
            onChange={e => setFilterMedecinId(e.target.value)}
            className="px-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue cursor-pointer text-gray-700"
          >
            <option value="">Tous les médecins</option>
            {medecins.map(m => (
              <option key={m.id} value={m.id}>Dr. {m.nom} {m.prenom}</option>
            ))}
          </select>
          <button
            onClick={() => navigate('/secretaire/nouveau-rendez-vous')}
            className="flex items-center gap-1.5 px-4 py-2 bg-clinic-green text-white text-xs font-semibold rounded-xl hover:bg-clinic-green-dark transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line text-sm"></i>
            Nouveau RDV
          </button>
        </div>
      </div>

      {/* ── Doctor legend ── */}
      {medecins.length > 0 && (
        <div className="flex flex-wrap gap-2 px-4 md:px-6 py-2.5 border-b border-gray-100 bg-gray-50/60">
          {medecins.map((m, i) => {
            const color = DOCTOR_COLORS[i % DOCTOR_COLORS.length];
            return (
              <div key={m.id} className="flex items-center gap-1.5">
                <span className={`w-3 h-3 rounded-sm ${color.block.split(' ')[0]}`}></span>
                <span className="text-xs text-gray-600 whitespace-nowrap">Dr. {m.nom} {m.prenom}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Calendar grid ── */}
      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          {/* Day headers */}
          <div className="flex border-b border-gray-200 bg-gray-50/50">
            {/* Time gutter */}
            <div className="w-14 md:w-16 shrink-0 border-r border-gray-200" />
            {weekDates.map((date, idx) => {
              const ds = toDateString(date);
              const isToday = ds === todayStr;
              const count = rdvCountPerDay[ds] ?? 0;
              return (
                <div
                  key={idx}
                  className={`flex-1 py-2.5 text-center border-r border-gray-200 last:border-r-0 ${isToday ? 'bg-clinic-blue/5' : ''}`}
                >
                  <div className={`text-xs font-semibold uppercase tracking-wide ${isToday ? 'text-clinic-blue' : 'text-gray-500'}`}>
                    {JOURS_COURT[date.getDay()]}
                  </div>
                  <div className={`mt-0.5 flex items-center justify-center gap-1.5`}>
                    <span className={`text-lg font-bold ${isToday ? 'text-clinic-blue bg-clinic-blue/10 w-8 h-8 rounded-full flex items-center justify-center text-sm' : 'text-gray-800'}`}>
                      {date.getDate()}
                    </span>
                    {count > 0 && (
                      <span className="w-5 h-5 bg-clinic-blue text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {count}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time + events grid */}
          <div className="flex" style={{ height: HOURS.length * PX_PER_HOUR }}>
            {/* Time labels column */}
            <div className="w-14 md:w-16 shrink-0 border-r border-gray-200">
              {HOURS.map(hour => (
                <div
                  key={hour}
                  style={{ height: PX_PER_HOUR }}
                  className="border-b border-gray-100 flex items-start justify-end pr-2 pt-1"
                >
                  <span className="text-[11px] text-gray-400 font-medium">
                    {String(hour).padStart(2, '0')}:00
                  </span>
                </div>
              ))}
            </div>

            {/* Day columns */}
            {weekDates.map((date, dIdx) => {
              const ds = toDateString(date);
              const isToday = ds === todayStr;
              const dayRdv = getRdvForDay(ds);

              return (
                <div
                  key={dIdx}
                  className={`flex-1 relative border-r border-gray-200 last:border-r-0 ${isToday ? 'bg-clinic-blue/3' : ''}`}
                  style={{ height: HOURS.length * PX_PER_HOUR }}
                >
                  {/* Hour grid lines + click zones */}
                  {HOURS.map(hour => (
                    <div
                      key={hour}
                      style={{ top: (hour - START_HOUR) * PX_PER_HOUR, height: PX_PER_HOUR }}
                      className="absolute inset-x-0 border-b border-gray-100 hover:bg-blue-50/30 transition-colors cursor-pointer z-0"
                      onClick={() => navigate(`/secretaire/nouveau-rendez-vous?date=${ds}&heure=${String(hour).padStart(2, '0')}:00`)}
                    />
                  ))}

                  {/* Half-hour lines */}
                  {HOURS.map(hour => (
                    <div
                      key={`half-${hour}`}
                      style={{ top: (hour - START_HOUR) * PX_PER_HOUR + PX_PER_HOUR / 2 }}
                      className="absolute inset-x-0 border-b border-dashed border-gray-100 pointer-events-none z-0"
                    />
                  ))}

                  {/* RDV blocks */}
                  {dayRdv.map(rdv => {
                    const { top, height } = getBlockStyle(rdv.heure_debut, rdv.heure_fin);
                    const colorIdx = doctorColorMap[rdv.medecin_id] ?? 0;
                    const color = DOCTOR_COLORS[colorIdx];
                    const isUrgent = rdv.type_rdv === 'urgent';
                    return (
                      <div
                        key={rdv.id}
                        style={{ top, height, position: 'absolute', left: 3, right: 3, zIndex: 10 }}
                        className={`${color.block} ${color.text} rounded-lg px-1.5 pt-0.5 pb-1 border cursor-pointer overflow-hidden transition-opacity hover:opacity-90 select-none`}
                        onClick={e => {
                          e.stopPropagation();
                          setSelectedRdv(rdv);
                        }}
                      >
                        {height >= 40 && (
                          <>
                            <div className="flex items-start justify-between gap-1">
                              <span className="text-[11px] font-bold leading-tight truncate">
                                {rdv.patients ? `${rdv.patients.prenom} ${rdv.patients.nom}` : 'Patient'}
                              </span>
                              {isUrgent && <i className="ri-alarm-warning-fill text-[11px] shrink-0"></i>}
                            </div>
                            {height >= 54 && (
                              <div className="text-[10px] opacity-90 truncate">
                                {rdv.heure_debut} – {rdv.heure_fin}
                              </div>
                            )}
                            {height >= 68 && rdv.medecins && (
                              <div className="text-[10px] opacity-80 truncate">
                                Dr. {rdv.medecins.nom}
                              </div>
                            )}
                          </>
                        )}
                        {height < 40 && (
                          <div className="text-[10px] font-semibold truncate leading-tight">
                            {rdv.patients ? `${rdv.patients.prenom} ${rdv.patients.nom[0]}.` : '•'} {rdv.heure_debut}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── RDV Detail Modal ── */}
      {selectedRdv && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedRdv(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header colored stripe */}
            <div className={`h-2 w-full ${DOCTOR_COLORS[doctorColorMap[selectedRdv.medecin_id] ?? 0].block.split(' ')[0]}`} />
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {selectedRdv.patients
                      ? `${selectedRdv.patients.prenom} ${selectedRdv.patients.nom}`
                      : 'Patient inconnu'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedRdv.medecins
                      ? `Dr. ${selectedRdv.medecins.nom} ${selectedRdv.medecins.prenom} — ${selectedRdv.medecins.specialite}`
                      : 'Médecin inconnu'}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedRdv(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
                >
                  <i className="ri-close-line text-gray-500"></i>
                </button>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <i className="ri-calendar-line text-clinic-blue text-base w-5 text-center"></i>
                  <span className="text-gray-700">
                    {new Date(selectedRdv.date_rdv + 'T00:00:00').toLocaleDateString('fr-FR', {
                      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                    })}
                  </span>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <i className="ri-time-line text-clinic-blue text-base w-5 text-center"></i>
                  <span className="text-gray-700">{selectedRdv.heure_debut} – {selectedRdv.heure_fin}</span>
                  {selectedRdv.type_rdv === 'urgent' && (
                    <span className="ml-auto px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                      URGENT
                    </span>
                  )}
                </div>

                {selectedRdv.patients?.telephone && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <i className="ri-phone-line text-clinic-blue text-base w-5 text-center"></i>
                    <span className="text-gray-700">{selectedRdv.patients.telephone}</span>
                  </div>
                )}

                {selectedRdv.patients?.email && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <i className="ri-mail-line text-clinic-blue text-base w-5 text-center"></i>
                    <span className="text-gray-700 break-all">{selectedRdv.patients.email}</span>
                  </div>
                )}

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <i className="ri-checkbox-circle-line text-clinic-blue text-base w-5 text-center"></i>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusBadge(selectedRdv.statut)}`}>
                    {getStatusLabel(selectedRdv.statut)}
                  </span>
                </div>
              </div>

              {(selectedRdv.statut === 'en_attente' || selectedRdv.statut === 'confirme') && onModify && (
                <div className="mt-5 flex gap-2">
                  <button
                    onClick={() => {
                      onModify(selectedRdv);
                      setSelectedRdv(null);
                    }}
                    className="flex-1 py-2.5 border border-clinic-blue/30 text-clinic-blue-dark rounded-xl text-sm font-semibold hover:bg-clinic-blue/5 transition-all cursor-pointer"
                  >
                    <i className="ri-edit-line mr-1.5"></i>Modifier
                  </button>
                  <button
                    onClick={() => setSelectedRdv(null)}
                    className="flex-1 py-2.5 bg-clinic-blue text-white rounded-xl text-sm font-semibold hover:bg-clinic-blue-dark transition-all cursor-pointer"
                  >
                    Fermer
                  </button>
                </div>
              )}

              {selectedRdv.statut !== 'en_attente' && selectedRdv.statut !== 'confirme' && (
                <button
                  onClick={() => setSelectedRdv(null)}
                  className="mt-5 w-full py-2.5 bg-clinic-blue text-white rounded-xl text-sm font-semibold hover:bg-clinic-blue-dark transition-all cursor-pointer"
                >
                  Fermer
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}